// FASE 05 — S4-B2/PO 2026-05-13: grid de listagem de tabelas de precos por tipo.
//
// Reteste do PO de 26/08/2026:
//  - item 4: a coluna "Empresa" agora vem preenchida (o endpoint Lista passou a devolver o campo);
//  - item 5: filtro na primeira linha do grid em Empresa, Nome da Tabela e Situacao;
//  - item 6: a coluna "Referencia" saiu — virou decorativa quando o PO fixou a referencia 36 (TP-1);
//  - item 7: com o Modo Edicao desligado, a acao da linha e CONSULTAR (produtos com preco da
//    versao publicada), no lugar de Editar/Excluir;
//  - item 2: icones alinhados a identidade visual das demais telas do MOF.

import React, { useMemo, useState } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { ITabelaPrecoListagem } from "../types/tabelaPreco.types";

interface GridListagemProps {
  tabelas: ITabelaPrecoListagem[];
  modoEdicao: boolean;
  carregando: boolean;
  onConsultar: (id: number) => void;
  onEditar: (id: number) => void;
  onExcluir: (id: number) => void;
}

const normalizar = (valor: string): string =>
  (valor || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// Cores de Chip por situacao da tabela.
const situacaoChipColor = (situacao: string): { bg: string; fg: string } => {
  // Normaliza removendo espacos e acentos para comparacao.
  const key = (situacao || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase();

  if (key.includes("emedicao")) return { bg: "#FFF3E0", fg: "#E65100" };
  if (key.includes("emhomologacao")) return { bg: "#E3F2FD", fg: "#1565C0" };
  // "Homologada" precisa ser testada DEPOIS de "Em Homologacao" — uma contem a outra.
  if (key.includes("homologada")) return { bg: "#EDE7F6", fg: "#4527A0" };
  if (key.includes("disponivel")) return { bg: "#E8F5E9", fg: "#2E7D32" };
  if (key.includes("indisponivel")) return { bg: "#FFEBEE", fg: "#C62828" };
  return { bg: "#F5F5F5", fg: "#424242" };
};

export const GridListagem: React.FC<GridListagemProps> = ({
  tabelas,
  modoEdicao,
  carregando,
  onConsultar,
  onEditar,
  onExcluir,
}) => {
  // Item 5 do reteste de 26/08: filtro na primeira linha do grid, no mesmo padrão
  // (FilterColumn) já usado em Aplicabilidades e Produtos N1.
  const [filtros, setFiltros] = useState({ empresa: "", nome: "", situacao: "" });

  const linhas = useMemo(() => {
    return tabelas.filter((t) => {
      if (filtros.empresa && !normalizar(t.empresa || "").includes(normalizar(filtros.empresa)))
        return false;
      if (filtros.nome && !normalizar(t.nome).includes(normalizar(filtros.nome))) return false;
      if (filtros.situacao && !normalizar(t.situacao).includes(normalizar(filtros.situacao)))
        return false;
      return true;
    });
  }, [tabelas, filtros]);

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: "empresa",
        headerName: "Empresa",
        flex: 1.2,
        minWidth: 180,
        sortable: true,
        renderHeader: () => (
          <FilterColumn
            label="Empresa"
            name="empresa"
            value={filtros.empresa}
            onChange={(e) => setFiltros((f) => ({ ...f, empresa: e.target.value }))}
          />
        ),
      },
      {
        field: "nome",
        headerName: "Nome da Tabela",
        flex: 1.8,
        minWidth: 240,
        sortable: true,
        renderHeader: () => (
          <FilterColumn
            label="Nome da Tabela"
            name="nome"
            value={filtros.nome}
            onChange={(e) => setFiltros((f) => ({ ...f, nome: e.target.value }))}
          />
        ),
      },
      {
        field: "dataAtualizacao",
        headerName: "Data Atualização",
        width: 160,
        sortable: true,
      },
      {
        field: "situacao",
        headerName: "Situação",
        width: 200,
        sortable: true,
        renderHeader: () => (
          <FilterColumn
            label="Situação"
            name="situacao"
            value={filtros.situacao}
            onChange={(e) => setFiltros((f) => ({ ...f, situacao: e.target.value }))}
          />
        ),
        renderCell: (params) => {
          const { bg, fg } = situacaoChipColor(params.value || "");
          return (
            <Chip
              size="small"
              label={params.value}
              sx={{ backgroundColor: bg, color: fg, fontWeight: 500 }}
            />
          );
        },
      },
    ];

    cols.push({
      field: "acoes",
      headerName: "Ações",
      width: 110,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params) =>
        modoEdicao ? (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Editar tabela">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditar(params.row.id);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir tabela">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onExcluir(params.row.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Tooltip title="Consultar produtos e preços">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onConsultar(params.row.id);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
    });

    return cols;
  }, [modoEdicao, onConsultar, onEditar, onExcluir, filtros]);

  if (!carregando && linhas.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 240,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px dashed #cfd8dc",
          borderRadius: 1,
          backgroundColor: "#fafafa",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {modoEdicao
            ? "Nenhuma tabela de preços cadastrada para este tipo."
            : "Nenhuma tabela de preços disponível para o mercado neste tipo."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
      <StripedDataGrid
        rows={linhas}
        columns={columns}
        loading={carregando}
        autoHeight
        checkboxSelection={false}
        pageSize={25}
        rowsPerPageOptions={[10, 25, 50, 100]}
        getRowId={(row) => row.id}
        headerHeight={72}
        sx={{
          border: "none",
          "& .MuiDataGrid-row.odd": { backgroundColor: "#EBF4F9" },
          "& .MuiDataGrid-row.even": { backgroundColor: "#fff" },
        }}
      />
    </Box>
  );
};
